/**
 * 売上の実績をまとめる。ここが唯一の出どころ。
 *
 * なぜ要るか:
 *   今の分析はスタッフ別・言語別が中心で、
 *   「今月いくら売れたか」を日ごと・月ごとに並べた表が無かった。
 *   店主がいちばん見たいのはそこ。
 *
 *   税抜・税額・割引の内訳も出していなかった。
 *   合計だけ出しても、店は税務や原価の話に使えない。
 *
 * 数え方:
 *   businessDate（'YYYY-MM-DD'）で束ねる。
 *   会計のときに営業日の区切りを当てたうえで作られている値なので、
 *   ここで区切りを当て直さない（二重に当てると1日ずれる）。
 *   料金画面・店舗の一覧と同じ数え方にしてある。
 */

/** 会計1件ぶん。画面の型に縛られないよう、必要な項目だけ受ける。 */
export interface CheckoutLike {
  businessDate?: string;
  subtotal?: number | null;
  tax?: number | null;
  discount?: number | null;
  total?: number | null;
  paymentFee?: number | null;
  guestCount?: { total?: number | null } | number | null;
  status?: string | null;
  paymentMethod?: string | null;
}

export interface SalesBucket {
  /** 'YYYY-MM-DD' か 'YYYY-MM' */
  key: string;
  /** 伝票の数 */
  slips: number;
  /** 客の数（分かるぶんだけ） */
  guests: number;
  /** 税抜の合計 */
  net: number;
  /** 消費税 */
  tax: number;
  /** 割引 */
  discount: number;
  /** 決済手数料 */
  fee: number;
  /** お会計の合計（税込） */
  gross: number;
  /** 客単価（客数が分かる伝票だけで出す） */
  perGuest: number;
  /** 伝票単価 */
  perSlip: number;
}

/** 取り消した伝票は売上に入れない。 */
export function isCountable(c: CheckoutLike): boolean {
  const s = c.status ?? '';
  return s !== 'voided' && s !== 'cancelled';
}

function guestsOf(c: CheckoutLike): number {
  const g = c.guestCount;
  if (typeof g === 'number') return g;
  if (g && typeof g === 'object') return Number(g.total ?? 0) || 0;
  return 0;
}

const n = (v: unknown) => Number(v) || 0;

/**
 * 会計をまとめる。
 *
 * @param by 'day' なら日ごと、'month' なら月ごと
 *
 * 空の期間も出す、といったことはここではしない。
 * 「その日は0円」と「その日のデータが無い」は違うので、
 * 画面側で必要に応じて埋める。
 */
export function summarizeSales(
  checkouts: CheckoutLike[],
  by: 'day' | 'month' = 'day',
): SalesBucket[] {
  const map = new Map<string, SalesBucket>();

  for (const c of checkouts) {
    if (!isCountable(c)) continue;
    const bd = c.businessDate;
    if (!bd || bd.length < 7) continue;   // 日付が無い伝票は数えられない
    const key = by === 'month' ? bd.slice(0, 7) : bd;

    const b = map.get(key) ?? {
      key, slips: 0, guests: 0, net: 0, tax: 0, discount: 0, fee: 0,
      gross: 0, perGuest: 0, perSlip: 0,
    };
    b.slips += 1;
    b.guests += guestsOf(c);
    b.net += n(c.subtotal);
    b.tax += n(c.tax);
    b.discount += n(c.discount);
    b.fee += n(c.paymentFee);
    b.gross += n(c.total);
    map.set(key, b);
  }

  // この設定では Map を展開して反復できないので、forEach で集める。
  const out: SalesBucket[] = [];
  map.forEach((v) => out.push(v));
  for (const b of out) {
    // 客数が入っていない伝票がある店では、客単価は出さない（0にする）。
    // 少ない客数で割ると、実際より高い客単価が出て誤解を生む。
    b.perGuest = b.guests > 0 ? Math.round(b.gross / b.guests) : 0;
    b.perSlip = b.slips > 0 ? Math.round(b.gross / b.slips) : 0;
  }
  // 新しい順。店主は「昨日いくら」から見る。
  out.sort((a, b) => b.key.localeCompare(a.key));
  return out;
}

/** 期間ぜんぶの合計 */
export function totalOf(buckets: SalesBucket[]): SalesBucket {
  const t: SalesBucket = {
    key: '合計', slips: 0, guests: 0, net: 0, tax: 0, discount: 0, fee: 0,
    gross: 0, perGuest: 0, perSlip: 0,
  };
  for (const b of buckets) {
    t.slips += b.slips; t.guests += b.guests; t.net += b.net;
    t.tax += b.tax; t.discount += b.discount; t.fee += b.fee; t.gross += b.gross;
  }
  t.perGuest = t.guests > 0 ? Math.round(t.gross / t.guests) : 0;
  t.perSlip = t.slips > 0 ? Math.round(t.gross / t.slips) : 0;
  return t;
}

/** 支払い方法ごとの内訳（現金がいくらか、を締めで使う） */
export function byPaymentMethod(checkouts: CheckoutLike[]): Array<{ method: string; slips: number; gross: number }> {
  const map = new Map<string, { method: string; slips: number; gross: number }>();
  for (const c of checkouts) {
    if (!isCountable(c)) continue;
    const m = c.paymentMethod ?? '不明';
    const e = map.get(m) ?? { method: m, slips: 0, gross: 0 };
    e.slips += 1;
    e.gross += n(c.total);
    map.set(m, e);
  }
  const list: Array<{ method: string; slips: number; gross: number }> = [];
  map.forEach((v) => list.push(v));
  return list.sort((a, b) => b.gross - a.gross);
}
